import { Link, useParams } from "react-router-dom";
import { Camera } from "lucide-react";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import { EmptyState, AskStatePill } from "@/design/components";
import { useSeed, useMoney, useOrgIndex } from "@/domain/hooks";
import { RaiseAsk } from "@/screens/reporter/RaiseAsk";
import { SubmitEvidence } from "@/screens/reporter/SubmitEvidence";

// Contractor-lead entry points into the act-and-evidence half of the trust loop. They
// reuse the canonical reporter wizards (one component, lens-scoped) inside the workspace
// shell, so the lead can raise, record and evidence from their own surface (P0.1).

export function WorkspaceRaiseAsk() {
  return (
    <WorkspaceShell>
      <RaiseAsk lensScope="contractor-lead" />
    </WorkspaceShell>
  );
}

export function WorkspaceSubmitEvidence() {
  return (
    <WorkspaceShell>
      <SubmitEvidence lensScope="contractor-lead" />
    </WorkspaceShell>
  );
}

export function WorkspaceEvidencePicker() {
  return (
    <WorkspaceShell>
      <EvidenceAskPicker />
    </WorkspaceShell>
  );
}

/** Pick an approved ask to evidence (the lead has no single ask context like the reporter). */
function EvidenceAskPicker() {
  const { workspaceId } = useParams();
  const seed = useSeed();
  const money = useMoney();
  const orgs = useOrgIndex();
  const awaiting = seed.spendRequests.filter((r) => r.state === "approved_for_spend" || r.state === "committed");

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Submit evidence</h1>
      <p className="mt-1 text-sm text-mute">Pick an approved ask to attach receipts, photos and notes against the spend.</p>
      <div className="mt-6">
        {awaiting.length === 0 ? (
          <EmptyState title="No asks awaiting evidence" body="Once an ask is approved for spend, it appears here for you to evidence." />
        ) : (
          <ul className="overflow-hidden rounded-card border border-line bg-card">
            {awaiting.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/workspace/${workspaceId}/submit-evidence/${r.id}`}
                  className="focus-ring flex items-center gap-3 border-b border-hairline px-4 py-3 last:border-0 hover:bg-subtle"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-infoSoft text-info">
                    <Camera className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{r.title}</p>
                    <p className="truncate text-xs text-faint">
                      {orgs[r.organisationId]?.name} · {seed.projects.find((p) => p.id === r.projectId)?.name}
                    </p>
                  </div>
                  <AskStatePill state={r.state} />
                  <span className="whitespace-nowrap text-sm font-medium tabular-nums text-ink">{money(r.amount)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
